/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'
import { CompanyContact } from 'types/entities/CompanyContact'
import { getCompanyContactDisplayName } from 'utils/companiesUtils'

interface LeadAccountContactConflictModalProps {
    contact: CompanyContact
    primaryContact?: CompanyContact | null
    /** When false, primary action keeps form contact fields without calling assign API. */
    canAssign?: boolean
    /** Soft link signal: contact email domain matches Account contacts/email/website. */
    emailDomainMatch?: string | null
    onAssign: () => void | Promise<void>
    onReplace: () => void | Promise<void>
    onCancel: () => void
}

const LeadAccountContactConflictModal: React.FC<LeadAccountContactConflictModalProps> = ({
    contact,
    primaryContact,
    canAssign = true,
    emailDomainMatch = null,
    onAssign,
    onReplace,
    onCancel,
}) => {
    const { t } = useTranslation()
    const [isSubmitting, setIsSubmitting] = React.useState(false)

    const contactName = getCompanyContactDisplayName(contact) || t('Account Contact')
    const primaryName = primaryContact
        ? getCompanyContactDisplayName(primaryContact)
        : t('No contact')

    const runAction = (action: () => void | Promise<void>) => {
        if (isSubmitting) {
            return
        }

        window.setTimeout(() => {
            const result = action()
            if (result && typeof (result as Promise<void>).then === 'function') {
                setIsSubmitting(true)
                ;(result as Promise<void>).finally(() => {
                    setIsSubmitting(false)
                })
            }
        }, 0)
    }

    return (
        <div className="LeadAccountContactConflictModal p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {t('Account Contact not linked')}
            </h3>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-8 space-y-2">
                <p>
                    {canAssign
                        ? t(
                              'Your Account Contact {{contactName}} is not assigned to this Account. Do you want to assign them to this Account, or replace them with the Account contact {{primaryName}}?',
                              { contactName, primaryName }
                          )
                        : t(
                              'You already filled Account Contact {{contactName}}. Do you want to keep it, or replace it with the Account contact {{primaryName}}?',
                              { contactName, primaryName }
                          )}
                </p>
                {emailDomainMatch ? (
                    <p className="rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-3 py-2">
                        {t(
                            'Email domain @{{domain}} matches contacts on this Account — assigning is recommended.',
                            { domain: emailDomainMatch }
                        )}
                    </p>
                ) : null}
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <UiButton
                    style={UiButtonStyle.Secondary}
                    size={UiButtonSize.Medium}
                    onClick={onCancel}
                    disabled={isSubmitting}
                >
                    {t('Cancel')}
                </UiButton>
                <UiButton
                    style={UiButtonStyle.Secondary}
                    size={UiButtonSize.Medium}
                    onClick={() => runAction(onReplace)}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                >
                    {primaryContact
                        ? t('Use Account contact {{name}}', { name: primaryName })
                        : t('Clear Account Contact')}
                </UiButton>
                <UiButton
                    style={UiButtonStyle.Primary}
                    size={UiButtonSize.Medium}
                    onClick={() => runAction(onAssign)}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                >
                    {canAssign ? t('Assign to Account') : t('Keep Account Contact')}
                </UiButton>
            </div>
        </div>
    )
}

export default LeadAccountContactConflictModal
